import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import ErrorBoundary from 'layout/components/ErrorBoundary';
import { useFormInstanceMutation } from 'shared/hooks/api/formFilling';
import FormEditView from 'form/components/FormEditView';
import FormCompletedView from 'form/components/FormCompletedView';
import { FormInstance } from 'types/step';
import { FormOrigin, FormPageLocationState } from 'form/types';
import { usePosthogCapture } from 'form/posthogCapture/_index';

import * as PageLayout from 'layout/styles/PageLayout.style';
import * as Styled from 'form/styles/Form.style';

interface FormPageProps {
  initialFormInstance?: FormInstance;
  initialIsCompleted?: boolean;
  origin?: FormOrigin;
  onBack?: () => void;
  embedded?: boolean;
}

export default function FormPage({
  initialFormInstance: initialFormInstanceProp,
  initialIsCompleted: initialIsCompletedProp,
  origin: originProp,
  onBack,
  embedded = false,
}: FormPageProps = {}): JSX.Element {
  const { t } = useTranslation('pageForm');
  const location = useLocation<FormPageLocationState | undefined>();
  const initialFormInstance =
    initialFormInstanceProp ?? location.state?.formInstance ?? null;
  const initialIsCompleted =
    initialIsCompletedProp ?? location.state?.isCompleted ?? false;
  const origin: FormOrigin = originProp ?? location.state?.origin ?? 'direct';
  const posthogCapture = usePosthogCapture();

  const [form, setForm] = useState<FormInstance | null>(initialFormInstance);
  const [isEditing, setIsEditing] = useState<boolean>(!initialIsCompleted);

  useEffect(() => {
    posthogCapture.pageViewed();
  }, [posthogCapture]);

  const templateName = form
    ? form.form_template.document_translation.form_template_name ||
      form.form_template.document_translation.title
    : '';

  const { mutate: updateFormInstance, isPending } = useFormInstanceMutation(
    form?.id || '',
    {
      onSuccess: (updated: FormInstance) => {
        setForm(updated);
        setIsEditing(false);
      },
      onError: (error: unknown) => {
        if (!form) return;
        posthogCapture.formSubmitError({
          templateId: form.form_template.id,
          templateName,
          error: error instanceof Error ? error.message : undefined,
        });
      },
    }
  );

  const PageWrapper = embedded ? React.Fragment : Styled.FormPage;

  if (!form) {
    return (
      <PageWrapper>
        <PageLayout.ContentContainer>
          <PageLayout.Content>
            <div>{t('completed.notFound')}</div>
          </PageLayout.Content>
        </PageLayout.ContentContainer>
      </PageWrapper>
    );
  }

  const handleSubmit = (filledData: Record<string, any>) => {
    setForm({ ...form, filled_data: filledData });
    setIsEditing(false);
    updateFormInstance({ filled_data: filledData });
  };

  return (
    <ErrorBoundary>
      <PageWrapper>
        {isEditing ? (
          <FormEditView
            form={form}
            origin={origin}
            onSubmit={handleSubmit}
            onBack={onBack}
            embedded={embedded}
          />
        ) : (
          <FormCompletedView
            form={form}
            origin={origin}
            onEdit={() => setIsEditing(true)}
            isLoading={isPending}
            onBack={onBack}
            embedded={embedded}
          />
        )}
      </PageWrapper>
    </ErrorBoundary>
  );
}
